import React from 'react';
import { useTranslation } from 'react-i18next';
import { Infinity as InfinityIcon, RefreshCw, Sparkles } from 'lucide-react';
import { useUsage } from '../../hooks/useUsage';
import { UsageResource } from '../../services/subscriptionService';

const RESOURCES: UsageResource[] = [
  'projects',
  'campaigns',
  'keywords',
  'competitors',
  'content_generations',
  'image_generations',
  'team_members',
  'automated_workflows'
];

interface Props {
  onUpgradeClick?: () => void;
}

// Painel "Uso do Plano": barras de progresso dos 8 recursos com CTA de upgrade.
const UsagePanel: React.FC<Props> = ({ onUpgradeClick }) => {
  const { t } = useTranslation();
  const { summary, isLoading, reload } = useUsage();

  const anyNearLimit = RESOURCES.some((r) => {
    const item = summary?.[r];
    return item && !item.unlimited && item.percentage >= 80;
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('subscription.usage.title', { defaultValue: 'Uso do Plano' })}
        </h3>
        <button
          onClick={() => reload()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {isLoading && !summary ? (
        <div className="space-y-4">
          {RESOURCES.map((r) => (
            <div key={r} className="h-10 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          {RESOURCES.map((resource) => {
            const item = summary?.[resource];
            if (!item) return null;
            const { current, limit, unlimited, percentage } = item;
            const reached = !unlimited && typeof limit === 'number' && current >= limit;
            const warning = !unlimited && !reached && percentage >= 80;
            const barColor = reached ? 'bg-red-500' : warning ? 'bg-amber-500' : 'bg-primary-500';
            const width = unlimited ? 0 : Math.min(percentage, 100);
            return (
              <div key={resource}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t(`subscription.usage.resources.${resource}`, { defaultValue: resource })}
                  </span>
                  <span className={`text-sm ${reached ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                    {unlimited ? (
                      <span className="inline-flex items-center gap-1">
                        <InfinityIcon className="w-4 h-4" />
                        {t('subscription.usage.unlimited', { defaultValue: 'Ilimitado' })}
                      </span>
                    ) : (
                      t('subscription.usage.used', { current, limit, defaultValue: `${current} de ${limit} usados` })
                    )}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${unlimited ? 'bg-primary-300 w-full opacity-30' : barColor}`}
                    style={unlimited ? undefined : { width: `${width}%` }}
                  />
                </div>
              </div>
            );
          })}

          {anyNearLimit && (
            <div className="mt-6 flex items-center justify-between gap-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t('subscription.usage.upgradeCta', { defaultValue: 'Precisa de mais? Contrate um plano superior para aumentar seus limites.' })}
              </p>
              <button
                onClick={onUpgradeClick}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                {t('subscription.limitExceeded.upgrade', { defaultValue: 'Fazer Upgrade' })}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsagePanel;
